import type { GetServerSideProps } from 'next'
import type { FC, ReactNode } from 'react'
import type { Lesson } from '@/prakrit/lib/lessons/types'
import type { LessonInfo } from '@/prakrit/components/learn/lesson-state'

import { Layout } from '@/components/generals/layout'
import { Seo } from '@/components/generals/seo'
import { CourseMap } from '@/prakrit/components/learn/course-map'
import { getLesson, lessonIds } from '@/prakrit/lib/content/lessons/registry'
import { lessonKeys } from '@/prakrit/lib/lessons/resolve'
import { clientLesson, lessonReviewKey } from '@/prakrit/lib/server/lessons'
import { statusMapSafe } from '@/prakrit/lib/server/review'

interface LearnPageProps {
    /** Lesson ids that exist in the registry; every other catalog lesson shows as "coming next". */
    available: string[]
    info: Record<string, LessonInfo>
}

const LearnPage: FC<LearnPageProps> = ({ available, info }): ReactNode => (
    <Layout>
        <Seo
            title='पाठ्यक्रम'
            description='The course map: every unit from the Prakrit alphabet to real Prakrit texts, with your progress. Your first sentence takes five minutes.'
        />
        <CourseMap available={available} info={info} />
    </Layout>
)

export const getServerSideProps: GetServerSideProps<LearnPageProps> = async () => {
    const available = lessonIds()
    const lessons = available.map(id => getLesson(id)).filter((l): l is Lesson => Boolean(l))

    const keysOf = (lesson: Lesson): string[] => {
        try {
            return lessonKeys(lesson)
        }
        catch {
            return []
        }
    }

    // One status lookup for every lesson and every form the lessons use.
    const map = await statusMapSafe(lessons.flatMap(lesson => [lessonReviewKey(lesson.id), ...keysOf(lesson)]))
    const info: Record<string, LessonInfo> = {}

    for (const lesson of lessons) {
        const status = map.get(lessonReviewKey(lesson.id)) ?? lesson.reviewStatus
        let playable = false

        try {
            playable = clientLesson(lesson, 'adults', status, map).playable
        }
        catch (error) {
            console.error('[learn] lesson does not resolve yet', lesson.id, error instanceof Error ? error.message : error)
        }

        info[lesson.id] = { status, playable }
    }

    return { props: { available, info } }
}

export default LearnPage
